import { type ReactNode, useEffect, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import {
  REAUTH_EVENT,
  SESSION_REFRESHED_EVENT,
  accessTokenSpent,
  installCrossTabSync,
  installVisibilityRefresh,
  refreshNow,
} from '@/lib/auth-refresh'
import { syncUserFromServer } from '@/features/auth/services/user-sync'
import { initEmbedBridge, isEmbedded } from '@/features/embed/embed-bridge'
import { bootstrapEmbeddedSession } from '@/features/embed/embed-auth'
import EmbedBlockedNotice from '@/features/embed/EmbedBlockedNotice'

interface AuthHydratorProps {
  children: ReactNode
}

/**
 * Runs once at app startup to rehydrate the auth store from the browser's
 * persistent storage. Ensures route guards see the correct status before
 * deciding whether to redirect.
 *
 * Also the app-wide home for session lifecycle: it listens for the refresher
 * giving up (REAUTH_REQUIRED) and clears the store, which drops the user to
 * the login route through the existing guards — no imperative navigation, so
 * this works from anywhere in the app.
 */
export default function AuthHydrator({ children }: AuthHydratorProps) {
  const hydrate = useAuthStore((s) => s.hydrate)
  const clear = useAuthStore((s) => s.clear)
  const applyRefreshedSession = useAuthStore((s) => s.applyRefreshedSession)
  const userId = useAuthStore((s) => s.user?.id)
  const [ready, setReady] = useState(false)
  // Framed by another application: who the user is comes from the host, not
  // from this browser's (partitioned, possibly stale) storage. `blocked` means
  // a site not on TRUSTED_EMBED_ORIGINS is framing us — render nothing of the app.
  const [embedBlocked, setEmbedBlocked] = useState(false)

  /**
   * Restore the session, renewing it first when the access token has aged out.
   *
   * A stored session whose ACCESS token expired is NOT a signed-out user: that
   * token lives fifteen minutes, while the HttpOnly refresh cookie behind it
   * lives thirty days. Storage used to delete the blob the moment the token
   * aged out, so closing the tab for longer than fifteen minutes presented a
   * login screen to someone whose session was entirely intact — and the refresh
   * that would have proved it was never attempted.
   *
   * The renewal is AWAITED rather than left to the timer because the guards and
   * the eight feature services both act the instant `ready` flips: releasing
   * them with a spent token means a burst of 401s before the first tick.
   * `refreshNow` fails closed on a 401 by firing REAUTH_EVENT — handled by the
   * effect below, which is registered by the time this resolves — and fails
   * OPEN on a network or 5xx error, retrying rather than signing anyone out.
   *
   * In a trusted frame the identity comes from the host app instead
   * (`bootstrapEmbeddedSession`); outside a frame this is unchanged.
   */
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const embed = await initEmbedBridge()
      if (embed === 'blocked') {
        if (!cancelled) setEmbedBlocked(true)
        return
      }
      if (embed === 'active') {
        await bootstrapEmbeddedSession()
      } else {
        hydrate()
        if (accessTokenSpent()) await refreshNow()
      }
      if (!cancelled) setReady(true)
    })()
    return () => {
      cancelled = true
    }
  }, [hydrate])

  // Top up the restored user from the server once an account is present. Keyed
  // on the id rather than run once on mount, so it covers BOTH paths that
  // produce a user: a reload (hydrate, above) and a fresh sign-in (the OIDC
  // callback calls setSession while this component is already mounted).
  // See `user-sync.ts` for why the auth payloads alone aren't enough.
  useEffect(() => {
    if (!userId) return
    void syncUserFromServer()
  }, [userId])

  useEffect(() => {
    const onReauth = () => clear()
    // A renewal keeps the STORED session fresh; this puts the same values into
    // the store, which is what the route guards actually read. Without it the
    // in-memory expiry stayed at the one issued at login, and a still-valid
    // session was bounced to /login on the next navigation after 15 minutes.
    const onRefreshed = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { session: { accessToken: string; refreshToken: string; expiresAt: number }; user?: Partial<{ id: string; email: string; name: string; avatarUrl?: string; role?: string }> }
        | undefined
      if (detail?.session) applyRefreshedSession(detail.session, detail.user)
    }
    window.addEventListener(REAUTH_EVENT, onReauth)
    window.addEventListener(SESSION_REFRESHED_EVENT, onRefreshed)
    // Timers don't fire reliably in a backgrounded tab, so a machine that slept
    // would wake holding an expired token. Renew on wake instead of waiting.
    const removeVisibility = installVisibilityRefresh()
    // Other tabs of this app share the session. Adopt their renewals rather
    // than racing them, and follow them out on a sign-out. Not in a frame: each
    // frame takes its identity from its own host, and following another tab's
    // frame would sign this meeting out the moment that one booted as a guest.
    const removeCrossTab = isEmbedded() ? () => {} : installCrossTabSync()
    return () => {
      window.removeEventListener(REAUTH_EVENT, onReauth)
      window.removeEventListener(SESSION_REFRESHED_EVENT, onRefreshed)
      removeVisibility()
      removeCrossTab()
    }
  }, [clear, applyRefreshedSession])

  if (embedBlocked) return <EmbedBlockedNotice />
  if (!ready) return null
  return <>{children}</>
}
